// One rule for every place the app navigates to a path it did not write
// itself (the login page's ?next=, a deep link, a stored "where was I").
//
// Why it is stricter than "starts with one slash": browsers read a backslash
// as a forward slash and silently drop tab / newline characters while parsing
// a URL, so "/" + backslash + "evil.com" means "//evil.com" — another website.
// react-router 6 hands such a path to history.pushState, which throws for a
// cross-origin URL, and then falls back to a full-page location.assign():
// an open redirect (GHSA-wrjc-x8rr-h8h6, fixed only in react-router 7.18).
// The guard below makes that advisory unreachable from our own inputs.

const BACKSLASH = String.fromCharCode(92);
const PARSE_BASE = "https://app.invalid";

const hasForbiddenChar = (path: string): boolean => {
  for (let i = 0; i < path.length; i++) {
    const code = path.charCodeAt(i);
    // C0 control characters (tab, newline, null …) and DEL.
    if (code <= 31 || code === 127) return true;
  }
  return path.includes(BACKSLASH);
};

export function isSafeInternalPath(path: unknown): path is string {
  if (typeof path !== "string" || path.length === 0) return false;
  if (path[0] !== "/" || path[1] === "/") return false;
  if (hasForbiddenChar(path)) return false;
  try {
    // Resolved against a fixed https base, NOT window.location: inside the
    // native app the page lives on capacitor://localhost, a scheme whose
    // URL().origin is the string "null", so comparing against the real
    // origin would refuse every deep link on iOS. What matters is only
    // whether the browser's parser keeps the path on the base's host.
    return new URL(path, PARSE_BASE).origin === PARSE_BASE;
  } catch {
    return false;
  }
}

export function safeInternalPath(path: unknown, fallback: string): string {
  return isSafeInternalPath(path) ? path : fallback;
}
